import { useMemo } from 'react';

/**
 * Count all leaf-level tabs in a menu tree (items with tab but no subMenu).
 */
const countLeafTabs = items =>
  items.reduce((count, item) => {
    if (item.subMenu?.length) return count + countLeafTabs(item.subMenu);
    return item.tab ? count + 1 : count;
  }, 0);

/**
 * Check if a search term matches a title or a list of keywords.
 */
const matchesTitleOrKeywords = (searchLower, title, keywords) => {
  if ((title || '').toLowerCase().includes(searchLower)) return true;
  // keywords are expected lowercase, but defensively handle mixed-case entries
  return !!keywords?.some(kw => kw.toLowerCase().includes(searchLower));
};

/**
 * Check if a search term matches an item.
 * Checks: display title → tab.keywords → item.keywords (for groups) →
 * subPages (pages nested under a flat tab that have no own menu entry, e.g.
 * ThreatCloud Feed under Knowledge Hub).
 */
const matchesSearch = (searchLower, item) => {
  if (matchesTitleOrKeywords(searchLower, item.tab?.title || item.title, item.tab?.keywords || item.keywords)) {
    return true;
  }

  return !!item.tab?.subPages?.some(sub => matchesTitleOrKeywords(searchLower, sub.title, sub.keywords));
};

/**
 * Filters menu items based on a search term.
 * - If a parent group matches, all its children are included.
 * - If a child matches, the parent is included with only matching children.
 * - Items without subMenu are included/excluded based on their own match.
 */
const filterMenuItems = (menuItems, searchLower) => {
  if (!searchLower) return menuItems;

  return menuItems
    .map(item => {
      const parentMatches = matchesSearch(searchLower, item);

      if (parentMatches) return item;

      if (!item.subMenu || item.subMenu.length === 0) {
        return null;
      }

      const filteredSubMenu = item.subMenu.filter(sub => matchesSearch(searchLower, sub));

      if (filteredSubMenu.length > 0) {
        return { ...item, subMenu: filteredSubMenu };
      }

      return null;
    })
    .filter(Boolean);
};

/**
 * Hook that filters navigation menu items based on a search term.
 * Returns the filtered list, search state flags, and leaf-level match counts.
 */
const useNavigationSearch = (menuItems, searchTerm) => {
  const isSearching = !!searchTerm?.trim();

  const filteredItems = useMemo(() => {
    const trimmed = searchTerm?.trim().toLowerCase();
    if (!trimmed) return menuItems;
    return filterMenuItems(menuItems, trimmed);
  }, [menuItems, searchTerm]);

  const totalCount = useMemo(() => countLeafTabs(menuItems), [menuItems]);
  const matchCount = useMemo(
    () => (isSearching ? countLeafTabs(filteredItems) : totalCount),
    [filteredItems, isSearching, totalCount]
  );

  return {
    filteredItems,
    isSearching,
    hasResults: filteredItems.length > 0,
    totalCount,
    matchCount
  };
};

export default useNavigationSearch;
